// Centralized TanStack Query key factory.
// Ensures consistent cache invalidation across the application.
// Extended in F02 (Task 109): added actions, history, schedules, rules,
// accountCodes, accountDetail, statement, workflow instances, dashboard, search.
// Extended in F28 (Task 135): added tasks (my, unassigned, pendingCount).
// Extended in F29 (Task 136): added config (tenantSettings, transactionLimits, workflowPolicies).
// Extended in Task 155: added gl.entries, gl.transactions.
// Extended in Task 164: added reference.workflowCategories.
// Extended in Task 167: added gl.documentCryptoEntries.
// Extended in Task 171: gl.balances accepts params; added gl.balanceAccountDetails.
// Task 359: gl.transactions/entries/balances/periods called WITHOUT params return the bare prefix.
//   `[..., 'transactions', undefined]` never matched a `[..., 'transactions', {params}]` query
//   (TanStack compares every position of the filter key), so invalidating after approving,
//   rejecting or reversing a journal left the ledger stale.

/** A list key with optional params; without params it is the prefix that matches every variant. */
const withParams = <K extends readonly unknown[]>(
  key: K,
  params: Record<string, unknown> | undefined,
): readonly unknown[] => (params === undefined ? key : [...key, params]);

export const queryKeys = {
  /** Task 387.11.6: the signed-in operator's own security settings. */
  account: {
    mfa: ['account', 'mfa'] as const,
    platformMfa: ['account', 'platform-mfa'] as const,
  },
  documentTypes: {
    all: ['document-types'] as const,
    tree: () => [...queryKeys.documentTypes.all, 'tree'] as const,
    /** Admin catalog (401.5): every type, active and inactive, unfiltered by view rights. */
    catalog: () => [...queryKeys.documentTypes.all, 'catalog'] as const,
    schema: (typeCode: string) => [...queryKeys.documentTypes.all, 'schema', typeCode] as const,
    transitions: (typeCode: string) =>
      [...queryKeys.documentTypes.all, 'transitions', typeCode] as const,
  },
  documents: {
    all: ['documents'] as const,
    list: (typeCode: string, filters?: Record<string, unknown>) =>
      [...queryKeys.documents.all, 'list', typeCode, filters] as const,
    detail: (id: string) => [...queryKeys.documents.all, 'detail', id] as const,
    actions: (id: string) => [...queryKeys.documents.all, 'detail', id, 'actions'] as const,
    history: (id: string) => [...queryKeys.documents.all, 'detail', id, 'history'] as const,
    /** A payment document's list projection (task 360.3 hero) — under detail, so it refreshes with it. */
    paymentProjection: (id: string) =>
      [...queryKeys.documents.all, 'detail', id, 'payment'] as const,
    attachments: (id: string) => [...queryKeys.documents.all, 'detail', id, 'attachments'] as const,
    /** Task 517.6: a payment's eligible correspondents (the bank it goes through) — under detail, refreshed with it. */
    paymentCorrespondents: (id: string) =>
      [...queryKeys.documents.all, 'detail', id, 'correspondents'] as const,
    /** A batch import's staged rows (task 422.9) — under detail, so a transition refreshes them. */
    batchRows: (id: string, filter: string, page: number) =>
      [...queryKeys.documents.all, 'detail', id, 'batch-rows', filter, page] as const,
    postingPreview: (id: string, page: number) =>
      [...queryKeys.documents.all, 'detail', id, 'posting-preview', page] as const,
    grants: (documentId: string, attachmentId: string) =>
      [
        ...queryKeys.documents.all,
        'detail',
        documentId,
        'attachments',
        attachmentId,
        'grants',
      ] as const,
  },
  customers: {
    all: ['customers'] as const,
    institutionTypes: ['customers', 'institution-types'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.customers.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.customers.all, 'detail', id] as const,
    addresses: (id: string) => [...queryKeys.customers.all, 'addresses', id] as const,
    limits: (id: string) => [...queryKeys.customers.all, 'limits', id] as const,
    accounts: (id: string) => [...queryKeys.customers.all, 'accounts', id] as const,
    accountRequests: (id: string) => [...queryKeys.customers.all, 'account-requests', id] as const,
    /** Task 387.12.4: App access state + operator commands. */
    appAccess: (id: string) => [...queryKeys.customers.all, 'app-access', id] as const,
  },
  fees: {
    all: ['fees'] as const,
    plans: () => [...queryKeys.fees.all, 'plans'] as const,
    planDetail: (id: string) => [...queryKeys.fees.all, 'plans', id] as const,
    schedules: (planId: string) => [...queryKeys.fees.all, 'schedules', planId] as const,
    rules: (scheduleId: string) => [...queryKeys.fees.all, 'rules', scheduleId] as const,
    planTree: (planId: string, activeOnly: boolean) =>
      [...queryKeys.fees.all, 'plan-tree', planId, activeOnly] as const,
    metadata: () => [...queryKeys.fees.all, 'metadata'] as const,
  },
  gl: {
    all: ['gl'] as const,
    accounts: () => [...queryKeys.gl.all, 'accounts'] as const,
    balances: (params?: Record<string, unknown>) =>
      withParams([...queryKeys.gl.all, 'balances'] as const, params),
    balanceAccountDetails: (params?: Record<string, unknown>) =>
      [...queryKeys.gl.all, 'balance-account-details', params] as const,
    accountCodes: () => [...queryKeys.gl.all, 'account-codes'] as const,
    accountDetail: (id: string) => [...queryKeys.gl.all, 'accounts', id] as const,
    /** Task 438: the account's "may go negative" rule — under the account, so its refresh includes it. */
    negativeBalance: (id: string) =>
      [...queryKeys.gl.all, 'accounts', id, 'negative-balance'] as const,
    /** Task 514.1: the account's debit / credit blocks — under the account, so its refresh includes them. */
    accountRestrictions: (id: string) =>
      [...queryKeys.gl.all, 'accounts', id, 'restrictions'] as const,
    /** Task 438.2: the platform chart with the tenant's layer. */
    tenantAccountCodes: () => [...queryKeys.gl.all, 'tenant-account-codes'] as const,
    accountBalance: (id: string) => [...queryKeys.gl.all, 'accounts', id, 'balance'] as const,
    /** Track 512 (ADR-074): the account's holds — under the account, so its refresh includes them. */
    accountHolds: (id: string, params?: Record<string, unknown>) =>
      [...queryKeys.gl.all, 'accounts', id, 'holds', params] as const,
    holdHistory: (holdId: string) => [...queryKeys.gl.all, 'holds', holdId, 'history'] as const,
    holdTypes: () => [...queryKeys.gl.all, 'hold-types'] as const,
    statement: (id: string, params?: Record<string, unknown>) =>
      [...queryKeys.gl.all, 'accounts', id, 'statement', params] as const,
    entries: (params?: Record<string, unknown>) =>
      withParams([...queryKeys.gl.all, 'entries'] as const, params),
    transactions: (params?: Record<string, unknown>) =>
      withParams([...queryKeys.gl.all, 'transactions'] as const, params),
    /** Task 486: one transaction — under the list prefix, so the list's invalidation refreshes it. */
    transactionDetail: (id: string) => [...queryKeys.gl.all, 'transactions', 'detail', id] as const,
    /** Task 459: the lazy row count of the grouped view (filters only, no page). */
    transactionsCount: (params?: Record<string, unknown>) =>
      withParams([...queryKeys.gl.all, 'transactions-count'] as const, params),
    /** Task 459: the lazy count + debit / credit sums of the journal view (filters only, no page). */
    entriesTotals: (params?: Record<string, unknown>) =>
      withParams([...queryKeys.gl.all, 'entries-totals'] as const, params),
    documentTransactions: (documentId: string) =>
      [...queryKeys.gl.all, 'document-transactions', documentId] as const,
    documentCryptoEntries: (documentId: string) =>
      [...queryKeys.gl.all, 'crypto-entries', documentId] as const,
    recalcPreflight: (params?: Record<string, unknown>) =>
      [...queryKeys.gl.all, 'recalc-preflight', params] as const,
    recalcJobStatus: (params?: Record<string, unknown>) =>
      [...queryKeys.gl.all, 'recalc-status', params] as const,
    /** Task 458.4: Pending / Running recalculation jobs (shell badge). */
    activeJobs: () => [...queryKeys.gl.all, 'jobs-active'] as const,
    transactionTypes: () => [...queryKeys.gl.all, 'transaction-types'] as const,
    health: () => [...queryKeys.gl.all, 'health'] as const,
    backdatedAccounts: () => [...queryKeys.gl.all, 'backdated-accounts'] as const,
    actualDates: () => [...queryKeys.gl.all, 'actual-dates'] as const,
    /** Task 363.1: revaluation gain / loss account configuration. */
    revaluationAccounts: () => [...queryKeys.gl.all, 'revaluation-accounts'] as const,
    /** Task 363.4: days needing re-revaluation. */
    staleRevaluationDays: () => [...queryKeys.gl.all, 'revaluation-stale'] as const,
    jobsHistory: (jobType: string, page: number, pageSize: number) =>
      [...queryKeys.gl.all, 'jobs-history', jobType, page, pageSize] as const,
    periods: (params?: Record<string, unknown>) =>
      withParams([...queryKeys.gl.all, 'periods'] as const, params),
    periodCheck: (date?: string) => [...queryKeys.gl.all, 'period-check', date] as const,
    transactionTypesByAccount: (accountId?: string) =>
      [...queryKeys.gl.all, 'transaction-types-by-account', accountId] as const,
    draftManualEntry: (id: string) => [...queryKeys.gl.all, 'manual-entries', id] as const,
    /** Task 450.7: what the type's form says per leg in a currency (prefill of the manual entry). */
    manualEntryHints: (transactionTypeId?: string, currencyCode?: string) =>
      [...queryKeys.gl.all, 'manual-entries', 'hints', transactionTypeId, currencyCode] as const,
    bankDetails: (accountId: string) =>
      [...queryKeys.gl.all, 'accounts', accountId, 'bankdetails'] as const,
    bankDetailsKinds: () => [...queryKeys.gl.all, 'bankdetails-kinds'] as const,
    bankDetailsRails: () => [...queryKeys.gl.all, 'bankdetails-rails'] as const,
  },
  fx: {
    all: ['fx'] as const,
    rates: (params?: Record<string, unknown>) => [...queryKeys.fx.all, 'rates', params] as const,
    snapshot: (params?: Record<string, unknown>) =>
      [...queryKeys.fx.all, 'snapshot', params] as const,
    position: () => [...queryKeys.fx.all, 'position'] as const,
  },
  reference: {
    all: ['reference'] as const,
    data: (source: string) => [...queryKeys.reference.all, source] as const,
    workflowCategories: () => [...queryKeys.reference.all, 'workflow-categories'] as const,
  },
  workflows: {
    all: ['workflows'] as const,
    list: () => [...queryKeys.workflows.all, 'list'] as const,
    /** Lightweight: code + name only, no states or transitions. */
    summaries: () => [...queryKeys.workflows.all, 'summaries'] as const,
    detail: (id: string) => [...queryKeys.workflows.all, 'detail', id] as const,
    statusGroups: () => [...queryKeys.workflows.all, 'status-groups'] as const,
    instances: (code: string) => [...queryKeys.workflows.all, code, 'instances'] as const,
    /** Task 495: the workflows a document type runs, with the reason. */
    byDocumentType: (typeCode: string) =>
      [...queryKeys.workflows.all, 'by-document-type', typeCode] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    overview: () => [...queryKeys.dashboard.all, 'overview'] as const,
    payments: () => [...queryKeys.dashboard.all, 'payments'] as const,
    compliance: () => [...queryKeys.dashboard.all, 'compliance'] as const,
  },
  // Extended in Task 176: financial reports
  reports: {
    all: ['reports'] as const,
    balanceSheet: (params?: Record<string, unknown>) =>
      [...['reports'], 'balance-sheet', params] as const,
    profitLoss: (params?: Record<string, unknown>) =>
      [...['reports'], 'profit-loss', params] as const,
    trialBalance: (params?: Record<string, unknown>) =>
      [...['reports'], 'trial-balance', params] as const,
    safeguarding: (params?: Record<string, unknown>) =>
      [...['reports'], 'safeguarding', params] as const,
    capitalRequirements: (params?: Record<string, unknown>) =>
      [...['reports'], 'capital-requirements', params] as const,
    snapshots: (params?: Record<string, unknown>) => [...['reports'], 'snapshots', params] as const,
    /** Track 512 (ADR-074 D18): the active-holds report. */
    holds: (params?: Record<string, unknown>) => [...['reports'], 'holds', params] as const,
    /** Task 522: the trial balance per account (keyset pages) and the report exports. */
    trialBalanceAccounts: (params?: Record<string, unknown>) =>
      [...['reports'], 'trial-balance-accounts', params] as const,
    exports: (params?: Record<string, unknown>) => [...['reports'], 'exports', params] as const,
    export: (id: string) => [...['reports'], 'exports', 'detail', id] as const,
    snapshotDetail: (id: string) => [...['reports'], 'snapshots', id] as const,
  },
  admin: {
    all: ['admin'] as const,
    /** Task 422.9: batch import profiles (global admin). */
    importProfiles: {
      list: () => ['admin', 'import-profiles', 'list'] as const,
      detail: (code: string, shared: boolean) =>
        ['admin', 'import-profiles', 'detail', code, shared] as const,
    },
    /** Task 382: the editor's chart — every code, inactive included, with usage. */
    chartOfAccounts: () => ['admin', 'chart-of-accounts'] as const,
    tenants: {
      all: ['admin', 'tenants'] as const,
      list: (params?: Record<string, unknown>) =>
        [...['admin', 'tenants'], 'list', params] as const,
      detail: (id: string) => [...['admin', 'tenants'], 'detail', id] as const,
      users: (id: string, params?: Record<string, unknown>) =>
        [...['admin', 'tenants'], 'users', id, params] as const,
    },
    users: {
      all: ['admin', 'users'] as const,
      list: (params?: Record<string, unknown>) => [...['admin', 'users'], 'list', params] as const,
      detail: (id: string) => [...['admin', 'users'], 'detail', id] as const,
      tenants: (id: string) => [...['admin', 'users'], 'tenants', id] as const,
      groups: (id: string) => [...['admin', 'users'], 'groups', id] as const,
      sessions: (id: string) => [...['admin', 'users'], 'sessions', id] as const,
    },
    groups: {
      all: ['admin', 'groups'] as const,
      list: (params?: Record<string, unknown>) => [...['admin', 'groups'], 'list', params] as const,
      detail: (id: string) => [...['admin', 'groups'], 'detail', id] as const,
      members: (id: string, params?: Record<string, unknown>) =>
        [...['admin', 'groups'], 'members', id, params] as const,
      roles: (id: string) => [...['admin', 'groups'], 'roles', id] as const,
    },
    roles: {
      all: ['admin', 'roles'] as const,
      list: (params?: Record<string, unknown>) => [...['admin', 'roles'], 'list', params] as const,
      detail: (code: string) => [...['admin', 'roles'], 'detail', code] as const,
    },
    invitations: {
      all: ['admin', 'invitations'] as const,
      list: (params?: Record<string, unknown>) =>
        [...['admin', 'invitations'], 'list', params] as const,
    },
  },
  invitations: {
    token: (token: string) => ['invitations', 'token', token] as const,
  },
  integration: {
    all: ['integration'] as const,
    dashboard: () => [...(['integration'] as const), 'dashboard'] as const,
    dlq: (p?: Record<string, unknown>) => [...(['integration'] as const), 'dlq', p] as const,
    connectors: () => [...(['integration'] as const), 'connectors'] as const,
    connectorHealth: (id: string) =>
      [...(['integration'] as const), 'connectors', id, 'health'] as const,
    requests: (p?: Record<string, unknown>) =>
      [...(['integration'] as const), 'requests', p] as const,
    requestDetail: (id: string) => [...(['integration'] as const), 'requests', id] as const,
    webhooks: (p?: Record<string, unknown>) =>
      [...(['integration'] as const), 'webhooks', p] as const,
    webhookDetail: (id: string) => [...(['integration'] as const), 'webhooks', id] as const,
    reconciliationRuns: () => [...(['integration'] as const), 'reconciliation-runs'] as const,
    reconciliationBreaks: (runId: string) =>
      [...(['integration'] as const), 'reconciliation-runs', runId] as const,
    tenantFlags: () => [...(['integration'] as const), 'tenant-flags'] as const,
    clearBankSettings: () => [...(['integration'] as const), 'clearbank-settings'] as const,
    connectorSigning: (connectorCode: string) =>
      [...(['integration'] as const), 'connectors', connectorCode, 'signing'] as const,
    connectorWebhookUrls: (connectorCode: string) =>
      [...(['integration'] as const), 'connectors', connectorCode, 'webhook-urls'] as const,
  },
  search: (q: string) => ['search', q] as const,
  /** Task 401.15: governed manual fallback flows (api 401.8–401.10). */
  manualFlows: {
    all: ['manual-flows'] as const,
    kycReadiness: () => [...(['manual-flows'] as const), 'kyc-readiness'] as const,
    inboundAccounts: (p?: Record<string, unknown>) =>
      [...(['manual-flows'] as const), 'inbound-accounts', p] as const,
    allocationAvailability: () =>
      [...(['manual-flows'] as const), 'allocation-availability'] as const,
  },
  config: {
    all: ['config'] as const,
    tenantSettings: () => [...['config'], 'tenant-settings'] as const,
    /** Task 439: per-product settlement accounts (card netting). */
    settlementProductRoutes: () => [...['config'], 'settlement-product-routes'] as const,
    /** Task 389. */
    reportMappings: () => [...['config'], 'report-mappings'] as const,
    transactionLimits: () => [...['config'], 'transaction-limits'] as const,
    workflowPolicies: () => [...['config'], 'workflow-policies'] as const,
  },
};
